import moment from "moment";

import { AdminCoverCropDetection } from "services";

import { CROP_TYPE_FCPO, CropFCPO } from "models";

export const COVER_CROP_DEFAULT_MIN_CONFIDENCE = 0.84;

export enum COVER_CROP_CHIP {
  CONFIRMED = "confirmed",
  NOT_DETECTED = "not_detected",
  MISSING = "missing",
}

export type CoverCropChip = {
  type: COVER_CROP_CHIP;
  // Confidence in the satellite's answer: the probability for "cover crop", 1 - probability for "no cover crop".
  confidence: number;
  // The farmer's cover crop the strip sits on. Not set for MISSING, which is its own tile.
  cropId?: string;
  detection: AdminCoverCropDetection;
};

// The model only watches July to March, so only cover crops sown July to December (or with no sowing date)
// count as the farmer's cover crop. Summer cover crops and undersowings count as "none added".
export const isWinterCoverCrop = (crop: CropFCPO) =>
  crop.cropType === CROP_TYPE_FCPO.COVER && (!crop.seedingStartDate || moment(crop.seedingStartDate).month() >= 6);

export const getCoverCropChip = (detection: AdminCoverCropDetection, cropsOfYear: CropFCPO[], minConfidence: number): CoverCropChip | null => {
  const probability = detection.coverCropProbability;

  // Rule-based winter cereal results never looked at the satellite.
  if (probability === null || detection.rotationType === "winter_cereal") {
    return null;
  }

  const saysCoverCrop = probability >= 0.5;
  const confidence = saysCoverCrop ? probability : 1 - probability;

  if (confidence < minConfidence) {
    return null;
  }

  const farmerCoverCrop =
    cropsOfYear.find((crop) => crop.id === detection.linkedFieldCropId && crop.cropType === CROP_TYPE_FCPO.COVER) ?? cropsOfYear.find(isWinterCoverCrop);

  if (saysCoverCrop) {
    return farmerCoverCrop
      ? { type: COVER_CROP_CHIP.CONFIRMED, confidence, cropId: farmerCoverCrop.id, detection }
      : { type: COVER_CROP_CHIP.MISSING, confidence, detection };
  }

  // Both say no cover crop: nothing to show.
  return farmerCoverCrop ? { type: COVER_CROP_CHIP.NOT_DETECTED, confidence, cropId: farmerCoverCrop.id, detection } : null;
};

export type CoverCropChipCounts = { detected: number; confirmed: number; missing: number; notDetected: number };

export const countCoverCropChips = (chips: (CoverCropChip | null)[]): CoverCropChipCounts => {
  const confirmed = chips.filter((chip) => chip?.type === COVER_CROP_CHIP.CONFIRMED).length;
  const missing = chips.filter((chip) => chip?.type === COVER_CROP_CHIP.MISSING).length;
  const notDetected = chips.filter((chip) => chip?.type === COVER_CROP_CHIP.NOT_DETECTED).length;

  return { detected: confirmed + missing, confirmed, missing, notDetected };
};

export const formatConfidence = (confidence: number) => `${Math.round(confidence * 100)}%`;

// The same years the crop timeline shows: current year - 5 up to next year.
export const getTimelineYears = () => {
  const currentYear = moment().year();

  return Array.from({ length: 7 }, (_, index) => currentYear - 5 + index);
};
